import { RouterStore as MobxStateRouterStore } from 'mobx-state-router';
import { RouterState } from 'core/utils/router/RouterState';
import { observable, computed, observe } from 'mobx';
import _ from 'lodash';

class RouterStore {
    routeHistory = [];
    router = null;
    transitionData = {};

    // route data definition per route
    // map in format: <route-name> : <{ title, crumbs: [{ title, route }]}>
    @observable accessor routeDataMap = observable.map([]);
    @observable accessor routerInitialized = false;

    get routes() { return this.router.routes; }

    @computed
    get routerState() { return this.router.routerState; }

    constructor(rootStore, routes) {

        this.rootStore = rootStore;

        // mobx-state-router v6: constructor signature is (routes, notFound, options).
        // rootStore moves into options.
        this.router = new MobxStateRouterStore(
            routes,
            new RouterState('master.not-found'),
            { rootStore }
        );
        // Legacy route hooks (flattenRoutes etc.) read routerStore.rootStore directly;
        // v6 only exposes it via options.rootStore, so mirror it here.
        this.router.rootStore = rootStore;

        // mobx-state-router v6 removed the central error hook that v4 exposed via
        // setErrorHook. Wrap the inner store's transition entry points so a genuine
        // (non-redirect) transition error from any source — app-initiated goTo,
        // browser back/forward, or the initial URL load via HistoryAdapter — is
        // routed through the handler registered in setErrorHook (which records the
        // error and navigates to the error route) instead of surfacing as an
        // unhandled rejection that strands the user on a blank route.
        const routeThroughErrorHook = (fn) => async (...args) => {
            try {
                return await fn(...args);
            } catch (ex) {
                if (this._errorHook && !this._handlingError) {
                    this._handlingError = true;
                    try {
                        return await this._errorHook(ex);
                    } finally {
                        this._handlingError = false;
                    }
                }
                throw ex;
            }
        };
        this.router.goTo = routeThroughErrorHook(this.router.goTo.bind(this.router));
        this.router.goToState = routeThroughErrorHook(this.router.goToState.bind(this.router));

        observe(this, 'routerState',
        // eslint-disable-next-line
            ({ oldValue, newValue }) => {
                var index = _.findIndex(this.routeHistory, {
                    routeName: newValue.routeName
                });

                if (!_.isNil(index) && index !== -1) {
                    // route exists in history, remove it so it can be added on top of stack (below)
                    this.routeHistory.splice(index, 1);
                }

                this.routeHistory.push(newValue);

                if (!this.routerInitialized) {
                    this.routerInitialized = true;
                }

                this.rootStore.menuStore.syncMenuRoute(newValue);

                if (
                    this.transitionData.forcePageRerender &&
                    oldValue.routeName === newValue.routeName
                ) {
                    this.rootStore.eventHandler.triggerEvent("onViewUpdate");
                }
            }
        );
    }

    async goTo(route, params, queryParams, options = null) {
        let newRoute;
        let newParams;
        let newQueryParams;
        // Accept both RouterState shim instances and plain mobx-state-router v6
        // state objects (created via createRouterState). Route history entries are
        // the latter shape, so an instanceof-only check would drop them through
        // to router.goTo(<object>) and resolve to notFound.
        if (route && typeof route === 'object' && route.routeName) {
            newRoute = route.routeName;
            newParams = route.params;
            newQueryParams = route.queryParams;
        } else {
            newRoute = route;
            newParams = params;
            newQueryParams = queryParams;
        }

        newParams = newParams || {};
        newQueryParams = newQueryParams || {};

        if (options) {
            this.transitionData = {
                ...this.transitionData,
                ...options
            };
        }
        // if new params don't have application identifier, old state had it and we are still on 'master.app' route, just
        // reuse old identifier to avoid whole application initialization logic
        // if (_.startsWith(newRoute, 'master.app') && !newParams.applicationIdentifier && this.routerState.params.applicationIdentifier) {
        //     newParams = {
        //         ...newParams,
        //         applicationIdentifier: this.routerState.params.applicationIdentifier
        //     }
        // }

        // mobx-state-router v6: goTo(name, { params, queryParams }) instead of v4 positional args.
        const newState = await this.router.goTo(newRoute, { params: newParams, queryParams: newQueryParams });
        this.transitionData = {};
        return newState;
    }

    goToNotFound() {
        return this.router.goToNotFound();
    }

    getCurrentRoute() {
        return this.router.getCurrentRoute();
    }

    getRoute(routeName) {
        if(routeName)
            return this.router.getRoute(routeName);
        return '';
    }

    setErrorHook(handler) {
        // mobx-state-router v6 removed setErrorHook. Stash the handler; the
        // transition wrappers installed in the constructor invoke it on a genuine
        // (non-redirect) transition error. Redirects are handled natively by v6
        // (beforeEnter returning a RouterState) / flattenRoutes.js.
        this._errorHook = handler;
    }

    isInitial() {
        return this.routeHistory.length <= 1;
    }

    goBack(steps = 1) {
        if (this.isInitial()) {
            const routeData = this.routeDataMap.get(this.routerState.routeName);
            if (routeData && routeData.back) {
                if (typeof routeData.back === 'function') {
                    return routeData.back();
                }

                const gotoData = this.routeHistory.find(i => i.routeName === this.routerState.routeName);

                return this.goTo(routeData.back, gotoData.params, gotoData.queryParams);
            }
            else {
                // route to dashboard if no back function present
                return this.goTo(this.rootStore.initialState);
            }
        } else {
            this.routeHistory.pop();

            // just a 'safe' transform if someone passes 'goBack' as onClick action
            // because then steps would be js event
            const numberOfSteps = typeof steps === 'number' ? steps : 1;

            // this allows to go back multiple steps
            const backStepsCount = this.routeHistory.length - numberOfSteps;
            const backSteps = backStepsCount < 0 ? 1 : backStepsCount;

            let newState = this.routeHistory[backSteps];
            if (newState) {
                // if there is already application on current state, route to that application
                // in case back leads to previous application and we don't want that
                if (this.routerState.params.applicationIdentifier) {
                    newState = new RouterState(newState.routeName, {
                        ...(newState.params || {}),
                        applicationIdentifier: this.routerState.params.applicationIdentifier,
                        ...(newState.queryParams || {})
                    });
                }

                this.goTo(newState);
            }
        }
    }

    setQueryParams(params) {
        return this.goTo(this.getCurrentRoute().name, this.routerState.params, params);
    }

    setTransitionData(data) {
        this.transitionData = {
            ...this.transitionData,
            ...(data || {})
        };
    }

    findRoute(name) {
        return _.find(this.router.routes, (route) => route.name === name);
    }

    setRouteTitle(routeTitleMap) {
        this.setRouteData(routeTitleMap, (value) => ({
            title: value
        }));
    }

    setRouteNavigateBack(routeBackMap) {
        this.setRouteData(routeBackMap, (value) => ({
            back: value
        }));
    }

    setRouteData(routeDataMap, getMergeObject) {
        _.forOwn(routeDataMap, (value, key) => {
            const routeData = this.routeDataMap.get(key) || {};
            const newRouteData = _.merge({}, routeData, getMergeObject(value));
            this.routeDataMap.set(key, newRouteData);
        });
    }

    getCurrentData() {
        return this.routeDataMap.get(this.routerState.routeName);
    }
}

export default RouterStore;
